import {InternalNode} from "crqlar-react-common/dist/models/profile/internalNode.model";
import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import {getProfileFullName, findProfileById} from "crqlar-react-common/dist/utils/profile.util";
import {
    GuestCount,
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    AgeCategory,
    AgeCategoryType
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import React, {useState} from "react";
import {
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from "@mui/material";
import Button from "@mui/material/Button";
import {
    getEventBackgroundColor,
    getEventColorBasedOnStatus
} from "crqlar-react-common/dist/utils/color.util";
import ReservationStatuses from "../reservationDialog/ReservationStatuses";
import {
    ReservationService
} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";

interface Props {

    ageCategories: AgeCategory[]

    localReservations: Reservation[]
    setLocalReservations: React.Dispatch<React.SetStateAction<Reservation[]>>
    loadedProfiles: Profile[]
    setMarkedReservation: React.Dispatch<React.SetStateAction<Reservation | undefined>>
}

const ServiceDialogReservations = ({

                                       ageCategories,
                                       localReservations,
                                       setLocalReservations,
                                       loadedProfiles,
                                       setMarkedReservation
                                   }: Props) => {

    const {t} = useLocalize()
    const noteGroupIndex = useNoteGroupIndex()
    const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedReservationForStatus, setSelectedReservationForStatus] = useState<Reservation | undefined>();


    const handleStatusChange = async (reservation: Reservation) => {
        await ReservationService.updateReservation(reservation);
        const updated = localReservations.map(r => r.id === reservation.id ? reservation : r);
        setLocalReservations(updated);
        setStatusAnchorEl(null);
    };

    const handleStatusClick = (e: React.MouseEvent<HTMLButtonElement>, reservation: Reservation) => {
        setStatusAnchorEl(e.currentTarget);
        setSelectedReservationForStatus(reservation);
    };

    const getProfileNames = (profileIds?: string[]) => {
        if (!profileIds || profileIds.length === 0) return '-'
        const names = profileIds
            .map(profileId => {
                const profile = findProfileById(loadedProfiles, profileId)
                return getProfileFullName(profile)
            })
            .filter(Boolean)
        return names.length === 0 ? '-' : names
    };

    const getRoomNumber = (profileIds?: string[], reservation?: Reservation) => {
        if (!profileIds || profileIds.length === 0 || !reservation) return '-'
        if (!reservation.expectedStartDate && !reservation.expectedStartDay) return '-'
        if (!reservation.expectedEndDate && !reservation.expectedEndDay) return '-'

        const profile = findProfileById(loadedProfiles, profileIds[0])
        if (!profile?.activeStays) return '-'

        const reservationStart = reservation.expectedStartDate
            ? new Date(reservation.expectedStartDate).getTime()
            : new Date(reservation.expectedStartDay!).getTime()

        const reservationEnd = reservation.expectedEndDate
            ? new Date(reservation.expectedEndDate).getTime()
            : new Date(reservation.expectedEndDay!).getTime()

        const matchingStay = profile.activeStays.find(stay => {
            const stayStart = new Date(stay.startDay!).getTime()
            const stayEnd = new Date(stay.endDay!).getTime()
            return reservationStart < stayEnd && reservationEnd > stayStart
        })

        return matchingStay?.roomId || '-'
    };

    const renderGuestCounts = (guestCounts?: GuestCount[]) => {
        if (!guestCounts || guestCounts.length === 0) return '-'

        const adultAgeCategories = ageCategories?.filter(ac => ac.type === AgeCategoryType.ADULT).map(ac => ac.id) || []
        const nonAdultAgeCategories = ageCategories?.filter(ac => ac.type !== AgeCategoryType.ADULT).map(ac => ac.id) || []

        const totalGuestCount = guestCounts.reduce((sum, gc) => sum + (gc.count || 0), 0)
        const adultGuestCount = guestCounts
            .filter(gc => adultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0)
        const nonAdultGuestCount = guestCounts
            .filter(gc => nonAdultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0)

        return `${totalGuestCount} (${adultGuestCount}/${nonAdultGuestCount})`
    };

    const renderNotes = (notes?: InternalNode[]) => {
        if (!notes || notes.length === 0) return '-'
        return notes.filter(note => !!note.note?.trim())
    };

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                        <TableCell>{t('Name')}</TableCell>
                        <TableCell>{t("Guest Count")}</TableCell>
                        <TableCell>{t('Status')}</TableCell>
                        <TableCell>{t('Notes')}</TableCell>
                        <TableCell>{t('Room Number')}</TableCell>
                        <TableCell className="service-dialog-actions-cell">{t('Actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {localReservations.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} style={{
                                textAlign: 'center',
                                padding: '16px'
                            }}>
                                {t('No reservations yet')}
                            </TableCell>
                        </TableRow>
                    )}

                    {localReservations.map((reservation) => {
                        const profileNames = getProfileNames(reservation.profileIds);
                        const notes = renderNotes(reservation.notes);
                        const roomNumber = getRoomNumber(reservation.profileIds, reservation);

                        return (
                            <TableRow key={reservation.id}>
                                <TableCell>
                                    {Array.isArray(profileNames) ? (
                                        <Stack spacing={0.5}>
                                            {profileNames.map((name, idx) => (
                                                <div key={idx}>{name}</div>
                                            ))}
                                        </Stack>
                                    ) : (
                                        profileNames
                                    )}
                                </TableCell>
                                <TableCell>{renderGuestCounts(reservation.guestCounts)}</TableCell>
                                <TableCell>
                                    <Button
                                        onClick={(e) => handleStatusClick(e, reservation)}
                                        sx={{
                                            color: getEventColorBasedOnStatus(reservation.status),
                                            backgroundColor: getEventBackgroundColor(reservation.status),
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {t(`Reservation status ${reservation.status}`)}
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    {Array.isArray(notes) ? (
                                        <Stack spacing={0.5}>
                                            {notes.map((note, idx) => (
                                                <Stack key={idx} direction={'row'}
                                                       alignItems={'flex-start'} gap={0.5}>
                                                    <NoteGroupIcon note={note} index={noteGroupIndex}
                                                                   fontSize={13}/>
                                                    <span style={{fontSize: '0.875rem', minWidth: 0}}>
                                                        {note.note}
                                                    </span>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    ) : (
                                        notes
                                    )}
                                </TableCell>
                                <TableCell>{roomNumber}</TableCell>
                                <TableCell className="service-dialog-actions-cell">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setMarkedReservation(reservation)}
                                    >
                                        {t('Edit')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Popover
                open={Boolean(statusAnchorEl)}
                anchorEl={statusAnchorEl}
                onClose={() => setStatusAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            >
                <Stack sx={{p: 2}}>
                    <ReservationStatuses
                        reservation={selectedReservationForStatus}
                        setReservation={handleStatusChange}
                        size="small"
                        direction="column"
                    />
                </Stack>
            </Popover>
        </>
    );
}

export default ServiceDialogReservations